/**
 * swimCard — the rules under the unified clubmate swim card (B-026, build 13).
 *
 * Build 15: a day-roster row opens ONE card for that swim (header, stats, the swim's sets as
 * the Swim tab's ribbon, the reactions row). Nothing opens by itself, and the tab no longer
 * feeds the card's compare zone; `compareZone` below stays for the card's own contract.
 * This module owns the data rules; ClubSwimDetail.svelte only binds them to markup.
 *
 * WHERE A SWIM'S SETS COME FROM (orchestrator ruling, 2026-09-14): a phone holds a
 * firehose for the viewer's newest swims and for each clubmate's LATEST swim only
 * (bridgeClubSessionSource). When the phone holds it, the sets are derived from it with
 * the swimmer's saved corrections, exactly like the Swim tab. Otherwise they come from
 * the club snapshot's C5 set summaries, expanded to one chip per length in the set's
 * C5 stroke: a mixed set draws MIXED chips, never invented per-length strokes. Both
 * render through the same ribbons, so the card has one grammar.
 *
 * COMPARABLE = same calendar day AND the swimmer's swim draws at least one length (C5 `sets`
 * non-empty, native's own no-lengths rule, or the firehose for an own swim older than the
 * snapshot, B-028). Nothing cleverer: no pool, no set names. Every compare row carries the
 * stroke caption: its chips never carry words, so the caption is always on.
 *
 * ORDER = the viewer pinned first (on a clubmate's card), then earliest start, then
 * name, then id. Never distance: that ranks. A swim whose start cannot be read (a
 * manually timed entry with no reliable start) sorts last within the day; C5 dates pass
 * the snapshot's ISO check, so on real data this only guards the sort.
 */
import type { LapLabel, SwimFirehoseResponse } from "@contract/types";
import type { ClubMember, ClubSnapshot, ClubSwim } from "./snapshot";
import type { SetGroup } from "../setGroups";
import type { MemberSwim } from "../clubSessions";
import type { Lap } from "../laps";
import { deriveWorkout } from "../lapAnalysis";
import { hasLengths } from "../sharedMorning";
import { captionFor } from "../setStrokeStrip";
import { fmtMeters, formatDuration } from "../format";
import { localDayKey, swimsByDay } from "./strip";
import type { LoadedSwim } from "./swimLoader";

/** Own row + 4 clubmates, then one inert "+N more swam today" line (decision 7). */
export const COMPARE_ROWS_VISIBLE = 5;

export interface SwimSets {
  groups: SetGroup[];
  /** Where the sets came from:
   *  - "firehose": derived like the Sets tab (a getClubSwimFirehose answer, or a firehose the session
   *    source holds);
   *  - "c5": the snapshot's set summaries, on a shell without getClubSwimFirehose (build 15 rule);
   *  - "summary": the same C5 summaries because this phone holds no firehose for the swim
   *    (notStored). The row says so (SUMMARY_LINE), never silently (build 16);
   *  - "none": an own swim only the session source knows (older than the snapshot's 14 days) that
   *    this phone holds no firehose for either. Its sets exist; they are not here;
   *  - "error": the fetch failed (LOAD_FAILED_LINE); "loading": not answered yet (LOADING_LINE). */
  source: "firehose" | "c5" | "summary" | "none" | "error" | "loading";
  /** A saved label changed a stroke on this swim (`dw.corrections.changed` non-empty). Only
   *  knowable from a firehose plus its labels; C5 and "none" are never marked (build 15). */
  edited: boolean;
  /** Own swims only (build 16): a correction that has not reached the club yet. */
  share: "queued" | "rejected" | null;
}

/** The session source's lookups, in its id space (the viewer is `viewerId`, not C5 `self`). */
export interface SwimSetLookup {
  viewerId: string;
  firehoseFor: (memberId: string, workoutUUID: string) => SwimFirehoseResponse | null;
  labelsFor?: (memberId: string, workoutUUID: string) => LapLabel[];
  /** Swim ids withOwnSessionSwims added: they carry no C5 sets to fall back on. */
  sessionOnly?: ReadonlySet<string>;
  /** Build 16: the shell answers getClubSwimFirehose, so every swim on the day is fetched by UUID
   *  (swimLoader) and `clubSwim` holds the answers. */
  loaderActive?: boolean;
  clubSwim?: (swimId: string) => LoadedSwim | undefined;
}

/** "Sets for this swim aren't on this phone": the no-firehose, no-C5 own swim (B-028). */
export const NOT_ON_PHONE_LINE = "Sets for this swim aren't on this phone";

/** Build 16 copy, for Scott's veto (docs/build-16.md). */
export const SUMMARY_LINE = "Estimated from summary";
export const LOAD_FAILED_LINE = "Couldn't load this swim's sets";
export const LOADING_LINE = "Loading sets…";
/** VP round 2, measured at 390 px (307 px for the line): the full sentence was 322 px and wrapped; the
 *  Club tab already says "the club". */
export const NOT_SHARED_LINE = "Not yet shared, sends on next open";
export const SHARE_FAILED_LINE = "Sharing failed, retries on next open";

/** Derived like the Sets tab. No lap events: no sets (B-019), never deriveWorkout's synthetic set. */
function derived(fh: SwimFirehoseResponse, labels: LapLabel[]): Pick<SwimSets, "groups" | "edited"> {
  if (!hasLengths(fh)) return { groups: [], edited: false };
  const dw = deriveWorkout(fh, labels);
  return { groups: dw.setGroups, edited: dw.corrections.changed.length > 0 };
}

/** One derivation per loader answer: the loader hands back the same answer object until a new one
 *  replaces it, and every answer that lands re-renders every row of the day. */
const derivedByAnswer = new WeakMap<LoadedSwim, Pick<SwimSets, "groups" | "edited">>();
function derivedAnswer(entry: Extract<LoadedSwim, { status: "ok" }>): Pick<SwimSets, "groups" | "edited"> {
  let hit = derivedByAnswer.get(entry);
  if (!hit) {
    hit = derived(entry.firehose, entry.labels);
    derivedByAnswer.set(entry, hit);
  }
  return hit;
}

function shareOf(value: string | null | undefined): SwimSets["share"] {
  return value === "queued" || value === "rejected" ? value : null;
}

export function setsForSwim(member: ClubMember, swim: ClubSwim, selfId: string, lookup: SwimSetLookup | null): SwimSets {
  const isSelf = member.id === selfId;
  const sourceId = isSelf ? lookup?.viewerId : member.id;
  const sessionOnly = lookup?.sessionOnly?.has(swim.id) ?? false;
  const held = lookup && sourceId ? lookup.firehoseFor(sourceId, swim.id) : null;
  const heldLabels = () => lookup!.labelsFor?.(sourceId!, swim.id) ?? [];

  if (lookup?.loaderActive) {
    const entry = lookup.clubSwim?.(swim.id);
    // Own swims carry their share state from C5 (members, or ownLabels via withOwnSessionSwims), re-sent
    // on every queue change; the bridge answer's shareState is a point-in-time copy and is not used here.
    const share = isSelf ? shareOf(swim.share) : null;
    const bare = (source: SwimSets["source"]): SwimSets => ({ groups: [], source, edited: false, share });
    if (entry?.status === "ok") return { ...derivedAnswer(entry), source: "firehose", share };
    if (entry?.status === "notStored") return sessionOnly ? bare("none") : { ...bare("summary"), groups: estimateGroups(swim) };
    if (entry?.status === "error") return bare("error");
    if (held) return { ...derived(held, heldLabels()), source: "firehose", share };
    return bare("loading");
  }

  if (!held) return sessionOnly ? { groups: [], source: "none", edited: false, share: null } : { groups: c5SetGroups(swim), source: "c5", edited: false, share: null };
  return { ...derived(held, heldLabels()), source: "firehose", share: null };
}

/** VP rounds 1-2: on an estimate, C5's "Mixed" is the bucket for lengths native could not resolve, not a
 *  stroke. Its chips read Unknown; the row's stroke tally leaves them out and `sourceLine` counts them
 *  ("24 lengths unlogged"), so MIXED keeps one meaning (a real stroke tally). */
function estimateGroups(swim: ClubSwim): SetGroup[] {
  return c5SetGroups(swim).map((g) => ({
    ...g,
    laps: g.laps.map((l) => (l.strokeStyle === "Mixed" ? { ...l, strokeStyle: "Unknown", watchStrokeStyle: "Unknown" } : l)),
  }));
}

/** One cue per meaning in the status slot (VP round 1): an estimate, something pending, an error. */
export type StatusKind = "estimate" | "pending" | "error";

export function sourceKind(sets: SwimSets): StatusKind | null {
  return sets.source === "summary" ? "estimate" : sets.source === "loading" ? "pending" : sets.source === "error" ? "error" : null;
}

export function shareKind(sets: SwimSets): StatusKind | null {
  return sets.share === "queued" ? "pending" : sets.share === "rejected" ? "error" : null;
}

/** The line under a swim's sets saying where they came from, or null for a derived swim. */
export function sourceLine(sets: SwimSets): string | null {
  switch (sets.source) {
    case "summary": {
      // VP round 2: the unresolved lengths leave the stroke tally and are counted here instead.
      const unlogged = sets.groups.reduce((n, g) => n + g.laps.filter((l) => l.strokeStyle === "Unknown").length, 0);
      // "· 24 unlogged", not "· 24 lengths unlogged": 324 px wrapped in the 307 px line at 390 px; 273 fits.
      return unlogged > 0 ? `${SUMMARY_LINE} · ${unlogged} unlogged` : SUMMARY_LINE;
    }
    case "error":
      return LOAD_FAILED_LINE;
    case "loading":
      return LOADING_LINE;
    default:
      return null;
  }
}

/** One short line on an own swim whose corrections have not reached the club; null otherwise. */
export function shareLine(sets: SwimSets): string | null {
  return sets.share === "queued" ? NOT_SHARED_LINE : sets.share === "rejected" ? SHARE_FAILED_LINE : null;
}

/** C5 names a set in `label`; a nameless set falls back to its stroke, then "Mixed set". */
export function clubSetName(set: { label: string; stroke: string }): string {
  const label = set.label.trim();
  if (label) return label;
  const stroke = set.stroke.trim();
  return stroke && stroke !== "Unknown" ? `${stroke} set` : "Mixed set";
}

function chip(index: number, stroke: string, meters: number | null): Lap {
  return {
    index,
    startMs: 0,
    endMs: 0,
    durationSeconds: 0,
    strokeStyle: stroke,
    watchStrokeStyle: stroke,
    strokeCount: null,
    spl: null,
    swolf: null,
    avgHr: null,
    maxHr: null,
    lengthMeters: meters,
    pacePer100s: null,
    effectiveExcluded: false,
    exclusionReason: null,
    correctionKey: { offsetSeconds: index, durationSeconds: 0, duplicateOrdinal: 0 },
  };
}

/** C5 `reps × distanceM` is the set's meters; on a known pool that is its lengths. */
export function c5SetGroups(swim: ClubSwim): SetGroup[] {
  const pool = (swim.poolM ?? 0) > 0 ? swim.poolM! : null;
  const groups: SetGroup[] = [];
  let next = 1;
  for (const set of swim.sets) {
    const meters = set.reps * set.distanceM;
    const lengths = pool ? Math.round(meters / pool) : set.reps;
    if (lengths <= 0) continue;
    const stroke = set.stroke.trim() || "Unknown";
    const laps = Array.from({ length: lengths }, (_, i) => chip(next + i, stroke, pool));
    next += lengths;
    const label = clubSetName(set);
    const bookend = label.toLowerCase();
    groups.push({
      index: groups.length + 1,
      laps,
      startMs: 0,
      endMs: 0,
      meters,
      durationSeconds: 0,
      restBeforeMs: null,
      label,
      isLoneLength: lengths === 1,
      setId: bookend === "warmup" || bookend === "cooldown" ? bookend : null,
    });
  }
  return groups;
}

/** "3 sets · 1,500 m in sets". */
export function setsFooter(groups: readonly SetGroup[]): string {
  const meters = groups.reduce((m, g) => m + g.meters, 0);
  return `${groups.length} ${groups.length === 1 ? "set" : "sets"} · ${fmtMeters(meters)} in sets`;
}

/** The no-lengths slot names what the watch did log (steering round 1 quick win). */
export function noLengthsLine(swim: ClubSwim): string {
  return `Watch logged ${formatDuration(swim.durationSec)}, no lengths`;
}

/** One chip row for a whole swim (the compare row's ribbon): every length, in order. */
export function rowSetGroup(laps: Lap[]): SetGroup {
  const meters = laps.reduce((m, l) => m + (l.lengthMeters ?? 0), 0);
  return { index: 1, laps, startMs: 0, endMs: 0, meters, durationSeconds: 0, restBeforeMs: null, label: "", isLoneLength: laps.length === 1, setId: null };
}

/** "FREE 6 · FLY 2": the row's strokes in words, since its chips are too small for them. Most lengths
 *  first, ties in the order the strokes first appear, so every row reads the same way (VP round 2). */
export function rowCaption(laps: readonly Lap[]): string {
  const order: string[] = [];
  const counts = new Map<string, number>();
  for (const lap of laps) {
    if (!counts.has(lap.strokeStyle)) order.push(lap.strokeStyle);
    counts.set(lap.strokeStyle, (counts.get(lap.strokeStyle) ?? 0) + 1);
  }
  const runs = order.map((stroke, first) => ({ stroke, n: counts.get(stroke)!, first }));
  runs.sort((x, y) => y.n - x.n || x.first - y.first);
  return captionFor(runs.map(({ stroke, n }) => ({ stroke, n })));
}

export interface CompareRow {
  member: ClubMember;
  name: string;
  isSelf: boolean;
  /** Every swim this swimmer did that day: the row opens their day card. */
  swims: ClubSwim[];
  /** "Terry · 1,200 m · 24 min": the comparable swims' totals, in words. */
  line: string;
  laps: Lap[];
  caption: string;
  startMs: number;
}

export interface ReasonLine {
  member: ClubMember;
  name: string;
  kind: "noLengths" | "notOnPhone" | "unsupported";
  /** "Terry swam 3,022 m, no lengths recorded": the distance the strip already shows (VP panel). */
  text: string;
}

export interface CompareZone {
  rows: CompareRow[];
  hiddenCount: number;
  overflowLine: string | null;
  reasons: ReasonLine[];
}

export interface CompareOptions {
  setsFor: (member: ClubMember, swim: ClubSwim) => SwimSets;
  /** A clubmate whose swims this build cannot read (CompanionSummary schema too new). */
  unsupported?: (memberId: string) => boolean;
}

export function nameOf(member: ClubMember, selfId: string): string {
  if (member.id === selfId) return "You";
  return member.name.trim() || "A clubmate";
}

export function startOf(swim: ClubSwim): number {
  const t = Date.parse(swim.date);
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}

/** The card's compare zone for `dayKey`, or null when nobody but the open card's
 *  swimmer swam that day (render nothing, not an empty section). */
export function compareZone(snapshot: ClubSnapshot, openMemberId: string, dayKey: string, opts: CompareOptions): CompareZone | null {
  const rows: CompareRow[] = [];
  const reasons: ReasonLine[] = [];
  let anyone = false;
  for (const member of snapshot.members) {
    if (member.id === openMemberId) continue;
    const swims = [...member.swims.filter((s) => localDayKey(Date.parse(s.date)) === dayKey)].sort((a, b) => startOf(a) - startOf(b));
    if (swims.length === 0) continue;
    anyone = true;
    const name = nameOf(member, snapshot.self);
    if (opts.unsupported?.(member.id)) {
      reasons.push({ member, name, kind: "unsupported", text: `${name} swam, on a newer version of Swim Set Club` });
      continue;
    }
    // "Logged a set" = the swim draws at least one length, from its firehose or its C5 sets
    // (for C5 this is exactly `sets` non-empty; a merged own swim has only its firehose, B-028).
    const drawn = swims.map((s) => ({ s, sets: opts.setsFor(member, s) }));
    const comparable = drawn.filter((d) => d.sets.groups.length > 0).map((d) => d.s);
    // A row joins every same-day swim, and each swim's lap indices restart at 1; SetGroupRibbon
    // keys chips by lap.index, so the row gets a reindexed COPY (1..n in swim order). The SETS
    // zone never merges: it draws one SetRibbon per swim, keyed within that swim.
    const laps = drawn.flatMap((d) => d.sets.groups.flatMap((g) => g.laps)).map((lap, i) => ({ ...lap, index: i + 1 }));
    if (laps.length === 0) {
      const swam = fmtMeters(swims.reduce((m, s) => m + s.distanceM, 0));
      const notHere = drawn.some((d) => d.sets.source === "none");
      reasons.push(
        notHere
          ? { member, name, kind: "notOnPhone", text: `${name} swam ${swam}, sets not on this phone` }
          : { member, name, kind: "noLengths", text: `${name} swam ${swam}, no lengths recorded` },
      );
      continue;
    }
    const distanceM = comparable.reduce((m, s) => m + s.distanceM, 0);
    const durationSec = comparable.reduce((m, s) => m + s.durationSec, 0);
    rows.push({
      member,
      name,
      isSelf: member.id === snapshot.self,
      swims: swimsByDay(member.swims).get(dayKey) ?? swims,
      line: `${name} · ${fmtMeters(distanceM)} · ${formatDuration(durationSec)}`,
      laps,
      caption: rowCaption(laps),
      startMs: startOf(comparable[0]),
    });
  }
  if (!anyone) return null;
  rows.sort(
    (a, b) =>
      Number(b.isSelf) - Number(a.isSelf) ||
      a.startMs - b.startMs ||
      a.name.localeCompare(b.name) ||
      a.member.id.localeCompare(b.member.id),
  );
  reasons.sort((a, b) => a.name.localeCompare(b.name) || a.member.id.localeCompare(b.member.id));
  const visible = rows.slice(0, COMPARE_ROWS_VISIBLE);
  const hiddenCount = rows.length - visible.length;
  return { rows: visible, hiddenCount, overflowLine: hiddenCount > 0 ? `+${hiddenCount} more swam today` : null, reasons };
}

export interface OpenCard {
  member: ClubMember;
  swims: ClubSwim[];
}

/**
 * B-028: the card's data = the C5 snapshot PLUS every own swim the session source carries.
 * Native cuts the viewer's own C5 swims to 14 days (ClubSnapshotSender.fetchSelfSwims), while
 * the session source (the calendar's and the date row's) carries up to 1,000 own workouts, so
 * without this an own swim older than 14 days had no card on its own day and no "You" row on
 * a clubmate's card. A swim both carry keeps its C5 copy (ids line up: own C5 id = workout
 * uuid). An added swim has no C5 sets and no pool; `sessionOnly` names them. Clubmates are
 * never added from the session source: C5 already carries their full history (B-016).
 */
export function withOwnSessionSwims(
  snapshot: ClubSnapshot,
  source: { viewerId: string; swims: readonly MemberSwim[] } | null,
): { snapshot: ClubSnapshot; sessionOnly: ReadonlySet<string> } {
  const sessionOnly = new Set<string>();
  if (!source) return { snapshot, sessionOnly };
  const self = snapshot.members.find((m) => m.id === snapshot.self);
  const known = new Set(self?.swims.map((s) => s.id) ?? []);
  const added: ClubSwim[] = [];
  for (const ms of source.swims) {
    if (ms.memberId !== source.viewerId || known.has(ms.swim.uuid)) continue;
    known.add(ms.swim.uuid);
    sessionOnly.add(ms.swim.uuid);
    // Build 16 (Codex P2-1): C5 ownLabels gives an older own swim the revision and share state its
    // members entry would carry, so an edit or a publish outcome invalidates it like any other swim.
    const own = snapshot.ownLabels?.[ms.swim.uuid];
    added.push({
      id: ms.swim.uuid,
      date: ms.swim.startDate,
      distanceM: ms.swim.totalDistance?.value ?? 0,
      durationSec: ms.swim.durationSeconds,
      poolM: null,
      sets: [],
      ...(own?.labelsRevision ? { labelsRevision: own.labelsRevision } : {}),
      ...(own?.share ? { share: own.share } : {}),
    });
  }
  if (added.length === 0) return { snapshot, sessionOnly };
  const members = self
    ? snapshot.members.map((m) => (m === self ? { ...m, swims: [...m.swims, ...added] } : m))
    : [...snapshot.members, { id: snapshot.self, name: "", swims: added, weekTotals: { swims: 0, distanceM: 0, durationSec: 0 } }];
  return { snapshot: { ...snapshot, members }, sessionOnly };
}

/** Build 15 (Scott 16:0x, "I love 2"): nothing opens by itself. The card on screen is the roster
 *  row the member picked, and only while the date row shows that swim's day. */
export function resolveOpenCard(picked: OpenCard | null, shownDay: string | null): OpenCard | null {
  const first = picked?.swims[0];
  if (!picked || !first || !shownDay) return null;
  return localDayKey(Date.parse(first.date)) === shownDay ? picked : null;
}
